import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, EditorContext } from '@tiptap/react';
import { Modal } from 'antd';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { SlashCommand, SlashCommandPluginKey } from './extensions/slashCommand';
import { AtMentionSuggestion, AtMentionSuggestionPluginKey } from './extensions/atMentionSuggestion';
import { createMarkdownPasteHandler } from './extensions/markdownPaste';
import { createCodeBlockExtension } from './extensions/codeBlockExtension';
import { handleCodeBlockTab } from './extensions/codeBlockTabIndent';
import {
  GlobalEditorShortcuts,
  TOGGLE_MARKDOWN_SOURCE_EVENT,
  isMarkdownToggleShortcut,
  onEditorEvent,
  offEditorEvent
} from './extensions/globalShortcuts';
import {
  BugherdMentionWithReactView,
  createBugherdLinkExtension,
  createRichTextEditorStarterKit,
  SubmitOnModEnterHardBreak
} from './extensions/tiptapShared';
import { consumeModEnterShortcut } from './utils/modEnterSubmit';
import { Toolbar } from './components/Toolbar';
import { RichTextBubbleMenu } from './components/RichTextBubbleMenu';
import { MentionPreviewContext } from './components/mentionPreviewContext';
import { useSlashCommandConfig, useAtMentionCommandConfig } from './useSlashCommand';
import { markdownToHtml, htmlToMarkdown } from './utils/serialization';
import { docToMarkdown, stateToMarkdown } from './utils/stateToMarkdown';
import { showDescriptionPasteLimitWarning } from './utils/descriptionPasteLimitWarning';
import { sidebarMarkdownToolbarDensity } from './utils/toolbarLayout';
import {
  resolveMentionPreviewContext,
  type EmojiPickerConfig,
  type MarkdownMentionPreviewContext,
  type ShortcutConfig
} from './types';
import { getEmptyTabFillText, shouldApplyEmptyTabFill } from './utils/emptyTabFill';
import {
  DESCRIPTION_CHARACTER_LIMIT
} from './utils/descriptionLimit';
import { DescriptionCharacterLimit } from './extensions/descriptionCharacterLimit';
import { clipDescriptionPaste } from './utils/clipDescriptionPaste';
import { applyDescriptionInsert, descriptionChangeAllowed, descriptionFits, descriptionLengths } from './utils/descriptionText';
import type { EditorView } from '@tiptap/pm/view';
import type { Slice } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import styles from './index.module.css';
import proseStyles from './richTextProse.module.css';

export type { EmojiPickerConfig };
export { DESCRIPTION_CHARACTER_LIMIT };

const MIN_DETAILS_HEIGHT = 120;

export type RichTextDescriptionProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When set, pressing Tab in an empty editor seeds this value as content. */
  emptyTabFillValue?: string | null;
  fullHeight?: boolean;
  autoFocus?: boolean;
  shortcutConfig?: ShortcutConfig | null;
  submitTask?: () => void;
  editorRef?: React.RefObject<HTMLDivElement | null>;
  emojiPickerConfig?: EmojiPickerConfig | null;
  mentionRenderContext?: MarkdownMentionPreviewContext | null;
  detailsMode?: boolean;
  saveOnBlur?: boolean;
  fixedWidthDetailsToolbarColumn?: boolean;
  footerLeadingActions?: React.ReactNode;
  footerActions?: React.ReactNode;
  inputDataCy?: string;
  /** Mount for overlays such as the paste-limit confirm. Sidebar passes the same node as Mark as. */
  getContainer?: () => HTMLElement;
};

export type { SidebarDetailsToolbarLayout } from './utils/toolbarLayout';
export { getSidebarDetailsToolbarLayout } from './utils/toolbarLayout';

export const RichTextDescription = ({
  value,
  onChange,
  placeholder = 'Add description',
  emptyTabFillValue = null,
  fullHeight = false,
  autoFocus = false,
  shortcutConfig = null,
  submitTask,
  footerLeadingActions,
  footerActions,
  emojiPickerConfig = null,
  mentionRenderContext = null,
  detailsMode = false,
  saveOnBlur = false,
  fixedWidthDetailsToolbarColumn: _fixedWidthDetailsToolbarColumn = false,
  inputDataCy,
  getContainer
}: RichTextDescriptionProps) => {
  const editorDataCy = inputDataCy ?? 'rich-text-description';
  const editorAttributes = {
    class: `${styles.editor} ${proseStyles.proseRoot}`,
    'data-cy': editorDataCy,
    ...(inputDataCy === 'create-task-description-input'
      ? { id: 'create-task-description-input' }
      : {})
  };
  const slashPopupContainerRef = React.useRef<HTMLDivElement>(null);
  const detailsEditorBoxRef = React.useRef<HTMLDivElement>(null);
  const footerActionsRef = useRef<HTMLDivElement>(null);
  const footerLeadingActionsRef = useRef<HTMLDivElement>(null);
  const markdownTextareaRef = useRef<HTMLTextAreaElement>(null);
  const hasAutoFocusedRef = useRef(false);
  const pendingSaveOnBlurRef = useRef<string | null>(null);
  const flushSaveOnBlurRef = useRef<() => void>(() => {});
  const prevShowMarkdownTextareaRef = useRef(false);
  /** BubbleMenu plugin keeps a stable `shouldShow` callback; read latest markdown mode here. */
  const showMarkdownTextareaRef = useRef(false);
  const commitOrQueueValueRef = useRef<(str: string) => void>(() => {});
  const handlePasteRef = useRef<
    (view: EditorView, event: ClipboardEvent, slice: Slice) => boolean
  >(() => false);
  const markdownPasteHandlerRef = useRef<(view: EditorView, event: Event) => boolean>(
    () => false
  );
  const getContainerRef = useRef(getContainer);
  getContainerRef.current = getContainer;
  const [modalApi, modalContextHolder] = Modal.useModal();
  const modalApiRef = useRef(modalApi);
  modalApiRef.current = modalApi;
  const [isFocused, setIsFocused] = useState(false);
  const [markdownEditPreferred, setMarkdownEditPreferred] = useState(false);
  const [markdownDraft, setMarkdownDraft] = useState<string | null>(null);
  const [isContentTall, setIsContentTall] = useState(false);
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);
  const [detailsEditorWidth, setDetailsEditorWidth] = useState(0);
  const shortcutConfigRef = React.useRef(shortcutConfig);
  shortcutConfigRef.current = shortcutConfig;

  const slashConfig = useSlashCommandConfig(shortcutConfig, slashPopupContainerRef);
  const atMentionConfig = useAtMentionCommandConfig(shortcutConfig, slashPopupContainerRef);

  const extensions = useMemo(
    () => [
      createRichTextEditorStarterKit({ submitOnModEnter: !!submitTask }),
      ...(submitTask ? [SubmitOnModEnterHardBreak] : []),
      createCodeBlockExtension(),
      Placeholder.configure({ placeholder }),
      Underline,
      Highlight,
      createBugherdLinkExtension({ openOnClick: true }),
      GlobalEditorShortcuts,
      Image,
      TaskList,
      TaskItem.configure({ nested: true }),
      BugherdMentionWithReactView,
      DescriptionCharacterLimit.configure({
        limit: DESCRIPTION_CHARACTER_LIMIT,
        onStorageLimit: () => modalApiRef.current.warning({
          title: 'Description has too much formatting',
          content: 'Remove some formatting or shorten link addresses to continue.',
          ...(getContainerRef.current ? { getContainer: getContainerRef.current } : {})
        })
      }),
      SlashCommand.configure({
        items: slashConfig.items,
        render: slashConfig.render
      }),
      AtMentionSuggestion.configure({
        items: atMentionConfig.items,
        render: atMentionConfig.render,
        atMentionDisabled: () => showMarkdownTextareaRef.current,
        shortcutConfigRef
      })
    ],
    [
      placeholder,
      slashConfig.items,
      slashConfig.render,
      atMentionConfig.items,
      atMentionConfig.render,
      submitTask
    ]
  );

  const promptOversizedPaste = useCallback(
    (view: EditorView, tr: Transaction): boolean => {
      const originalState = view.state;
      const clipped = clipDescriptionPaste(view, tr);
      const canInsert = clipped !== null;
      showDescriptionPasteLimitWarning({
        modal: modalApiRef.current,
        canInsert,
        storageLimit: descriptionLengths(docToMarkdown(tr.doc, originalState.schema)).stored > 4000,
        getContainer: getContainerRef.current,
        onInsert: canInsert
          ? () => { if (view.state === originalState && clipped) view.dispatch(clipped); }
          : undefined
      });
      return true;
    },
    []
  );

  handlePasteRef.current = (view, event, slice) => {
    if (showMarkdownTextareaRef.current) return false;
    const tr = view.state.tr.replaceSelection(slice);
    const nextMarkdown = docToMarkdown(tr.doc, view.state.schema);
    if (descriptionChangeAllowed(nextMarkdown, stateToMarkdown(view.state))) return false;
    event.preventDefault();
    return promptOversizedPaste(view, tr);
  };

  markdownPasteHandlerRef.current = createMarkdownPasteHandler(
    showMarkdownTextareaRef,
    {
      characterLimit: DESCRIPTION_CHARACTER_LIMIT,
      fits: descriptionFits,
      getNextMarkdown: (view, tr) => docToMarkdown(tr.doc, view.state.schema),
      onOversizedPaste: (view, _event, _nextMarkdown, tr) =>
        promptOversizedPaste(view, tr)
    }
  );

  const editor = useEditor({
    extensions,
    content: markdownToHtml(value),
    editorProps: {
      attributes: editorAttributes,
      handlePaste: (view, event, slice) =>
        handlePasteRef.current(view, event, slice),
      handleKeyDown: (view, event) => {
        if (handleCodeBlockTab(view, event)) return true;
        const slashSuggestionState = SlashCommandPluginKey.getState(view.state) as
          | { active?: boolean }
          | undefined;
        const atMentionSuggestionState = AtMentionSuggestionPluginKey.getState(view.state) as
          | { active?: boolean }
          | undefined;
        if (shouldApplyEmptyTabFill({
          key: event.key,
          emptyTabFillValue,
          currentValueLength: view.state.doc.textContent.length,
          slashSuggestionActive: Boolean(slashSuggestionState?.active),
          atMentionSuggestionActive: Boolean(atMentionSuggestionState?.active)
        })) {
          event.preventDefault();
          event.stopPropagation();
          const valueToInsert = getEmptyTabFillText(
            emptyTabFillValue,
            DESCRIPTION_CHARACTER_LIMIT
          );
          const { from, to } = view.state.selection;
          view.dispatch(view.state.tr.insertText(valueToInsert, from, to));
          return true;
        }
        if (consumeModEnterShortcut(event)) {
          if (submitTask) {
            submitTask();
            return true;
          }
          if (saveOnBlur) {
            view.dom.blur();
            return true;
          }
        }
        return false;
      },
      handleDOMEvents: {
        paste: (view, event) => markdownPasteHandlerRef.current(view, event)
      }
    },
    immediatelyRender: false
  });

  const lastEmittedValueRef = React.useRef<string | symbol>(Symbol('initial'));

  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = value;
    editor.commands.setContent(markdownToHtml(value), false);
  }, [value, editor]);

  useEffect((): (() => void) | void => {
    if (!editor) return;

    if (!saveOnBlur) {
      const syncMarkdown = () => {
        const str = htmlToMarkdown(editor.getHTML());
        if (descriptionFits(str) || descriptionChangeAllowed(str, value)) {
          lastEmittedValueRef.current = str;
          onChange(str);
        }
      };
      editor.on('update', syncMarkdown);
      return (): void => { editor.off('update', syncMarkdown); };
    }

    const trackDraft = () => {
      const str = htmlToMarkdown(editor.getHTML());
      if (descriptionFits(str) || descriptionChangeAllowed(str, value)) {
        pendingSaveOnBlurRef.current = str;
      }
    };

    editor.on('update', trackDraft);

    const flush = () => {
      const pending = pendingSaveOnBlurRef.current;
      if (pending !== null && pending !== lastEmittedValueRef.current) {
        lastEmittedValueRef.current = pending;
        onChange(pending);
      }
      pendingSaveOnBlurRef.current = null;
    };

    flushSaveOnBlurRef.current = flush;

    return (): void => {
      flush();
      editor.off('update', trackDraft);
      flushSaveOnBlurRef.current = () => {};
    };
  }, [editor, onChange, saveOnBlur, value]);

  const showMarkdownTextarea = isFocused && markdownEditPreferred;
  showMarkdownTextareaRef.current = showMarkdownTextarea;

  const handleContainerFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      const next = e.relatedTarget as Node | null;
      if (
        next &&
        e.currentTarget.contains(next) &&
        !footerActionsRef.current?.contains(next) &&
        !footerLeadingActionsRef.current?.contains(next)
      ) return;

      if (saveOnBlur) {
        flushSaveOnBlurRef.current();
      }

      if (markdownEditPreferred && editor) {
        const md = markdownDraft !== null ? markdownDraft : value;
        if (descriptionFits(md) || descriptionChangeAllowed(md, value)) {
          editor.commands.setContent(markdownToHtml(md), false);
        }
      }

      setMarkdownDraft(null);
      setIsFocused(false);
    },
    [saveOnBlur, markdownEditPreferred, editor, markdownDraft, value]
  );

  /** Emits immediately (or queues for blur-flush), dropping edits over the character limit. */
  const commitOrQueueValue = useCallback(
    (str: string) => {
      if (!descriptionFits(str) && !descriptionChangeAllowed(str, value)) return;
      if (saveOnBlur) {
        pendingSaveOnBlurRef.current = str;
      } else {
        lastEmittedValueRef.current = str;
        onChange(str);
      }
    },
    [saveOnBlur, onChange, value]
  );
  commitOrQueueValueRef.current = commitOrQueueValue;


  const checkMarkdownTextareaHeight = useCallback((ta: HTMLTextAreaElement | null) => {
    if (!ta) return;
    setIsContentTall(ta.scrollHeight > MIN_DETAILS_HEIGHT + 10);
  }, []);

  const handleMarkdownTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      if (!descriptionChangeAllowed(next, markdownDraft ?? value)) {
        if (descriptionLengths(next).stored > 4000) {
          modalApiRef.current.warning({
            title: 'Description has too much formatting',
            content: 'Remove some formatting or shorten link addresses to continue.',
            ...(getContainerRef.current ? { getContainer: getContainerRef.current } : {})
          });
        }
        return;
      }
      setMarkdownDraft(next);
      commitOrQueueValue(next);
      checkMarkdownTextareaHeight(e.target);
    },
    [commitOrQueueValue, checkMarkdownTextareaHeight, markdownDraft, value]
  );

  const applyMarkdownSourceChange = useCallback(
    (next: string, selectionStart: number, selectionEnd: number) => {
      if (!descriptionChangeAllowed(next, markdownDraft ?? value)) {
        if (descriptionLengths(next).stored > 4000) {
          modalApiRef.current.warning({
            title: 'Description has too much formatting',
            content: 'Remove some formatting or shorten link addresses to continue.',
            ...(getContainerRef.current ? { getContainer: getContainerRef.current } : {})
          });
        }
        return;
      }
      const ta = markdownTextareaRef.current;
      if (ta) {
        ta.value = next;
        ta.focus();
        ta.setSelectionRange(selectionStart, selectionEnd);
      }
      setMarkdownDraft(next);
      commitOrQueueValue(next);
      checkMarkdownTextareaHeight(ta);
    },
    [commitOrQueueValue, checkMarkdownTextareaHeight, markdownDraft, value]
  );

  const handleMarkdownTextareaPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const ta = e.currentTarget;
      const insertText = e.clipboardData.getData('text/plain');
      const result = applyDescriptionInsert(ta.value, ta.selectionStart, ta.selectionEnd, insertText);
      if (!result.clipped) return;

      e.preventDefault();
      showDescriptionPasteLimitWarning({
        modal: modalApiRef.current,
        canInsert: result.canInsert,
        storageLimit: descriptionLengths(ta.value.slice(0, ta.selectionStart) + insertText + ta.value.slice(ta.selectionEnd)).stored > 4000,
        getContainer: getContainerRef.current,
        onInsert: result.canInsert
          ? () =>
              applyMarkdownSourceChange(
                result.nextValue,
                result.cursor,
                result.cursor
              )
          : undefined
      });
    },
    [applyMarkdownSourceChange]
  );

  const markdownSourceContext = useMemo(
    () => ({
      getValue: () => markdownDraft ?? value,
      getSelection: () => {
        const ta = markdownTextareaRef.current;
        return { start: ta?.selectionStart ?? 0, end: ta?.selectionEnd ?? 0 };
      },
      apply: applyMarkdownSourceChange
    }),
    [markdownDraft, value, applyMarkdownSourceChange]
  );

  const handleEmojiInsertInMarkdownSource = useCallback(
    (native: string) => {
      const current = markdownDraft !== null ? markdownDraft : value;
      const ta = markdownTextareaRef.current;
      const selectionStart = ta?.selectionStart ?? current.length;
      const selectionEnd = ta?.selectionEnd ?? current.length;
      const result = applyDescriptionInsert(current, selectionStart, selectionEnd, native);
      if (result.clipped) return;
      applyMarkdownSourceChange(result.nextValue, result.cursor, result.cursor);
    },
    [markdownDraft, value, applyMarkdownSourceChange]
  );

  const handleMarkdownToggle = useCallback(() => {
    if (!editor) return;
    if (markdownEditPreferred) {
      const md = markdownDraft !== null ? markdownDraft : htmlToMarkdown(editor.getHTML());
      if (descriptionFits(md) || descriptionChangeAllowed(md, value)) {
        lastEmittedValueRef.current = md;
        if (saveOnBlur) {
          pendingSaveOnBlurRef.current = md;
          flushSaveOnBlurRef.current();
        } else {
          onChange(md);
        }
        editor.commands.setContent(markdownToHtml(md), false);
      }
      setMarkdownEditPreferred(false);
      setMarkdownDraft(null);
    } else {
      const md = htmlToMarkdown(editor.getHTML());
      setMarkdownDraft(md);
      setMarkdownEditPreferred(true);
      if (saveOnBlur && descriptionFits(md)) {
        pendingSaveOnBlurRef.current = md;
      }
    }
  }, [editor, markdownEditPreferred, markdownDraft, saveOnBlur, onChange]);

  useEffect(() => {
    if (!autoFocus || !editor || hasAutoFocusedRef.current) return;

    const activeElement = document.activeElement;
    const root = slashPopupContainerRef.current;
    const shouldMoveFocus =
      !activeElement ||
      activeElement === document.body ||
      (root ? root.contains(activeElement) : false);

    if (!shouldMoveFocus) return;

    hasAutoFocusedRef.current = true;
    requestAnimationFrame(() => {
      if (showMarkdownTextarea) {
        markdownTextareaRef.current?.focus();
        return;
      }
      editor.chain().focus().run();
    });
  }, [autoFocus, editor, showMarkdownTextarea]);

  useEffect((): (() => void) | void => {
    if (!editor) return;
    onEditorEvent(editor, TOGGLE_MARKDOWN_SOURCE_EVENT, handleMarkdownToggle);
    return (): void => offEditorEvent(editor, TOGGLE_MARKDOWN_SOURCE_EVENT, handleMarkdownToggle);
  }, [editor, handleMarkdownToggle]);

  useLayoutEffect(() => {
    if (!editor) return;
    if (showMarkdownTextarea && !prevShowMarkdownTextareaRef.current) {
      const ta = markdownTextareaRef.current;
      if (ta) ta.focus();
      checkMarkdownTextareaHeight(ta);
    } else if (
      !showMarkdownTextarea &&
      prevShowMarkdownTextareaRef.current &&
      isFocused
    ) {
      editor.chain().focus().run();
    }
    prevShowMarkdownTextareaRef.current = showMarkdownTextarea;
  }, [showMarkdownTextarea, editor, isFocused, checkMarkdownTextareaHeight]);

  useEffect((): (() => void) | void => {
    if (!editor || !detailsMode) return;
    const el = editor.view?.dom as HTMLElement | null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const checkHeight = () => setIsContentTall(el.scrollHeight > MIN_DETAILS_HEIGHT + 10);
    checkHeight();
    const observer = new ResizeObserver(checkHeight);
    observer.observe(el);
    return (): void => observer.disconnect();
  }, [editor, detailsMode]);

  useLayoutEffect((): void | (() => void) => {
    if (!detailsMode || !editor) return;
    const box = detailsEditorBoxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const update = () => setDetailsEditorWidth(box.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box);
    return () => observer.disconnect();
  }, [detailsMode, editor]);

  const providerValue = useMemo(() => ({ editor }), [editor]);

  const resolvedMentionUiContext = useMemo(
    () =>
      resolveMentionPreviewContext({
        ...(mentionRenderContext ?? {}),
        descriptionMarkdown: value
      }),
    [mentionRenderContext, value]
  );

  const measuredToolbarWidth = detailsEditorWidth;

  const toolbarDensity = useMemo(
    () =>
      !detailsMode || fullHeight
        ? null
        : sidebarMarkdownToolbarDensity(measuredToolbarWidth),
    [detailsMode, fullHeight, measuredToolbarWidth]
  );
  const toolbarPanelCompact = toolbarDensity != null;
  const toolbarSidebarTightViewport = toolbarDensity === 'tight';
  const toolbarSidebarFillWidth = toolbarDensity === 'compact';
  const toolbarNarrowViewportScroll = false;

  if (!editor) return null;

  const isExpanded = isFocused || isContentTall || isManuallyExpanded;
  const showExpandToggle = detailsMode && !fullHeight && isContentTall && !isFocused;
  const charCount = descriptionLengths(
    showMarkdownTextarea ? (markdownDraft ?? value) : htmlToMarkdown(editor.getHTML())
  ).visible;

  const markdownToolbarProps = {
    markdownEditPreferred,
    markdownSourceModeActive: showMarkdownTextarea,
    markdownSource: showMarkdownTextarea ? markdownSourceContext : null,
    onMarkdownToggle: handleMarkdownToggle,
    onEmojiInsertInMarkdownSource: showMarkdownTextarea ? handleEmojiInsertInMarkdownSource : undefined
  };

  const markdownTextarea = showMarkdownTextarea ? (
    <textarea
      ref={markdownTextareaRef}
      className={`${styles.editor} ${styles.markdownTextarea}`}
      value={markdownDraft !== null ? markdownDraft : value}
      onChange={handleMarkdownTextareaChange}
      onPaste={handleMarkdownTextareaPaste}
      onKeyDown={(e) => {
        if (isMarkdownToggleShortcut(e)) {
          e.preventDefault();
          handleMarkdownToggle();
          return;
        }
        if (shouldApplyEmptyTabFill({
          key: e.key,
          emptyTabFillValue,
          currentValueLength: e.currentTarget.value.length
        })) {
          e.preventDefault();
          e.stopPropagation();
          const valueToInsert = getEmptyTabFillText(
            emptyTabFillValue,
            DESCRIPTION_CHARACTER_LIMIT
          );
          setMarkdownDraft(valueToInsert);
          commitOrQueueValue(valueToInsert);
          checkMarkdownTextareaHeight(e.currentTarget);
          return;
        }
        if (consumeModEnterShortcut(e)) {
          if (submitTask) {
            submitTask();
            return;
          }
          if (saveOnBlur) {
            (e.target as HTMLTextAreaElement).blur();
          }
        }
      }}
      placeholder={placeholder}
      aria-label={placeholder}
      data-cy={inputDataCy ?? 'rich-text-description-markdown'}
      id={inputDataCy === 'create-task-description-input' ? 'create-task-description-input' : undefined}
    />
  ) : null;

  const sharedBubbleMenu = (
    <RichTextBubbleMenu
      editor={editor}
      showMarkdownTextareaRef={showMarkdownTextareaRef}
      appendToRef={slashPopupContainerRef}
    />
  );

  const editorAndTextarea = (
    <div className={styles.editorWrapper}>
      <EditorContent
        editor={editor}
        className={showMarkdownTextarea ? styles.editorHidden : styles.editorInner}
      />
      {markdownTextarea}
    </div>
  );

  return (
    <EditorContext.Provider value={providerValue}>
      <MentionPreviewContext.Provider value={resolvedMentionUiContext}>
        <div
          ref={slashPopupContainerRef}
          className={`${styles.container} ${fullHeight ? styles.fullHeight : ''}`}
          style={{ position: 'relative' }}
          onFocus={handleContainerFocus}
          onBlur={handleContainerBlur}
        >
          {detailsMode ? (
            <>
              <div
                ref={detailsEditorBoxRef}
                className={[
                  styles.detailsEditorBox,
                  isFocused ? styles.detailsFocused : '',
                  isExpanded && !fullHeight ? styles.detailsExpanded : '',
                  isManuallyExpanded && !fullHeight ? styles.detailsManualExpanded : '',
                  fullHeight ? styles.detailsEditorBoxFullHeight : ''
                ].filter(Boolean).join(' ')}
              >
                {editorAndTextarea}
                {isFocused && (
                  <div
                    className={styles.detailsToolbarDock}
                    onMouseDown={e => {
                      if ((e.target as HTMLElement).closest('input, textarea')) return;
                      e.preventDefault();
                    }}
                  >
                    <Toolbar
                      variant="bottom"
                      emojiPickerConfig={emojiPickerConfig}
                      panelCompact={toolbarPanelCompact}
                      sidebarTightViewport={toolbarSidebarTightViewport}
                      sidebarFillToolbarWidth={toolbarSidebarFillWidth}
                      narrowViewportToolbarScroll={toolbarNarrowViewportScroll}
                      {...markdownToolbarProps}
                    />
                  </div>
                )}
              </div>
              {showExpandToggle && (
                <button
                  type="button"
                  className={styles.expandToggle}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => setIsManuallyExpanded(v => !v)}
                >
                  {isManuallyExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </>
          ) : (
            <>
              <Toolbar emojiPickerConfig={emojiPickerConfig} {...markdownToolbarProps} />
              {editorAndTextarea}
            </>
          )}
          <div className={styles.footer}>
            <span className={styles.count}>{charCount} / {DESCRIPTION_CHARACTER_LIMIT}</span>
            {footerLeadingActions && (
              <div
                ref={footerLeadingActionsRef}
                onFocus={event => event.stopPropagation()}
              >
                {footerLeadingActions}
              </div>
            )}
            {!detailsMode && submitTask && (
              <span className={styles.submitHint}>
                {navigator.platform.indexOf('Mac') > -1 ? 'Cmd' : 'Ctrl'} + Enter to submit
              </span>
            )}
            {footerActions && (
              <div
                ref={footerActionsRef}
                className={styles.footerActions}
                onFocus={event => event.stopPropagation()}
              >
                {footerActions}
              </div>
            )}
          </div>
          {sharedBubbleMenu}
          {modalContextHolder}
        </div>
      </MentionPreviewContext.Provider>
    </EditorContext.Provider>
  );
};

export default RichTextDescription;
